/**
 * `useOemSourceChip()` — the click handler behind an answer's source chips.
 *
 * `./sources.ts` decides *where* a chip label leads; this hook performs it.
 * It is the one piece of the port that needs React, and it exists so the
 * foundation's `ChatMessages.tsx` can stay a two-line `// fork(oem):` seam
 * rather than growing a drawer/router/toast dependency of its own.
 *
 * Three outcomes, matching {@link SourceResolution}:
 *
 * | Resolution | What happens |
 * |---|---|
 * | `drawer` | opens the `oem-source` drawer on the table slice |
 * | `route` | navigates the main pane |
 * | `none` | raises a toast carrying the label — the demo's `toast(s)` |
 *
 * A failed data fetch is caught and toasted rather than thrown: a citation
 * click must never take the rail down.
 *
 * UI layer (layer 4 of the 4-layer architecture) — a binding, no derivation.
 *
 * @see ./sources.ts — `resolveSource()`
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrawer } from '@/app/providers';
import { useToast } from '../toast/ToastContext.tsx';
import { resolveSource } from './sources.ts';

/** Returns a handler taking the chip's label. */
export function useOemSourceChip(): (label: string) => void {
  const { open } = useDrawer();
  const navigate = useNavigate();
  const { show: toast } = useToast();

  return useCallback(
    (label: string) => {
      void resolveSource(label)
        .then((res) => {
          if (res.kind === 'drawer') {
            open('oem-source', {
              kicker: 'SOURCE',
              title: res.payload.title,
              wide: true,
              data: res.payload as unknown as Record<string, unknown>,
            });
            return;
          }
          if (res.kind === 'route') {
            navigate(res.to);
            return;
          }
          toast(res.label);
        })
        .catch(() => toast(`Could not open ${label}`));
    },
    [navigate, open, toast],
  );
}
